/**
 * HeroPhotoRotator — the hero's photo frame.
 *
 * Shows one photo at a time and slowly crossfades through the set. Kept
 * deliberately quiet: one frame, no arrows, no captions. The user can pause
 * or pick a photo; hovering pauses; `prefers-reduced-motion` disables the
 * auto-advance entirely (the first photo just stays put).
 *
 * SSR/prerender-safe: nothing here touches `window` during render.
 */

import { useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface HeroPhoto {
    src: string;
    webp?: string;
    alt: string;
    /** CSS object-position for the focal point when the photo is cropped by object-cover. */
    position?: string;
}

interface HeroPhotoRotatorProps {
    photos: HeroPhoto[];
    /** Time each photo stays up before the next crossfade. */
    intervalMs?: number;
    /**
     * `frame` — a self-contained rounded 2:1 photo card.
     * `fill`  — no frame: fills its (positioned) parent edge to edge, for use
     *           as a section backdrop. The parent decides size and masking.
     */
    variant?: 'frame' | 'fill';
    className?: string;
    /** Extra classes for every <img> (e.g. an object-position for the focal point). */
    imageClassName?: string;
    /** Extra classes for the dots/pause cluster (e.g. to move it in fill mode). */
    controlsClassName?: string;
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(REDUCED_MOTION_QUERY).matches
        : false;

export const HeroPhotoRotator = ({
    photos,
    intervalMs = 7000,
    variant = 'frame',
    className,
    imageClassName,
    controlsClassName,
}: HeroPhotoRotatorProps) => {
    const fill = variant === 'fill';
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);

    // Track the OS setting live (it can change while the page is open).
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mq = window.matchMedia(REDUCED_MOTION_QUERY);
        const update = () => setReducedMotion(mq.matches);
        update();
        mq.addEventListener?.('change', update);
        return () => mq.removeEventListener?.('change', update);
    }, []);

    const rotating = photos.length > 1 && !paused && !hovered && !reducedMotion;

    useEffect(() => {
        if (!rotating) return;
        const id = window.setInterval(() => {
            setIndex((current) => (current + 1) % photos.length);
        }, intervalMs);
        return () => window.clearInterval(id);
    }, [rotating, intervalMs, photos.length]);

    const showControls = photos.length > 1;

    return (
        <div
            className={cn(fill ? 'absolute inset-0' : 'relative', className)}
            data-variant={variant}
            role="group"
            aria-roledescription="carousel"
            aria-label="Our gear in use"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div
                className={cn(
                    'overflow-hidden bg-secondary',
                    fill
                        ? 'absolute inset-0'
                        : 'relative aspect-[2/1] rounded-[1.75rem] border border-white/70 shadow-[0_24px_56px_rgba(54,49,43,0.16)]',
                )}
            >
                {photos.map((photo, i) => {
                    const active = i === index;
                    return (
                        <div
                            key={photo.src}
                            role="group"
                            aria-roledescription="slide"
                            aria-label={`Photo ${i + 1} of ${photos.length}`}
                            aria-hidden={!active}
                            className={cn(
                                'absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none',
                                active ? 'opacity-100' : 'opacity-0',
                            )}
                        >
                            <picture>
                                {photo.webp && <source srcSet={photo.webp} type="image/webp" />}
                                <img
                                    src={photo.src}
                                    alt={photo.alt}
                                    width={1200}
                                    height={600}
                                    className={cn('h-full w-full object-cover', imageClassName)}
                                    style={photo.position ? { objectPosition: photo.position } : undefined}
                                    decoding="async"
                                    loading={i === 0 ? 'eager' : 'lazy'}
                                    // React 18 only forwards the lowercase form of this attribute.
                                    {...(i === 0 ? { fetchpriority: 'high' } : {})}
                                />
                            </picture>
                        </div>
                    );
                })}
            </div>

            {showControls && (
                <div
                    className={cn(
                        'absolute bottom-3 right-3 flex items-center gap-0.5 rounded-full bg-foreground/40 p-1 backdrop-blur-sm',
                        controlsClassName,
                    )}
                >
                    {photos.map((photo, i) => {
                        const active = i === index;
                        return (
                            <button
                                key={photo.src}
                                type="button"
                                aria-label={`Show photo ${i + 1} of ${photos.length}`}
                                aria-current={active ? 'true' : undefined}
                                onClick={() => {
                                    setIndex(i);
                                    setPaused(true);
                                }}
                                className="flex h-8 w-8 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                            >
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        'block h-2 rounded-full transition-[width,background-color] duration-300',
                                        active ? 'w-5 bg-white' : 'w-2 bg-white/60',
                                    )}
                                />
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        aria-label={paused ? 'Play photo rotation' : 'Pause photo rotation'}
                        aria-pressed={paused}
                        onClick={() => setPaused((current) => !current)}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                        {paused ? (
                            <Play className="h-3.5 w-3.5" aria-hidden="true" />
                        ) : (
                            <Pause className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};
